/* Sindhi Cart Page — controller
 * ──────────────────────────────────────────────────────────────────────────
 * Vanilla JS (no custom element) driving the redesigned /cart page rendered
 * by sections/sindhi-cart-page.liquid. Deliberately NOT the <sindhi-cart-drawer>
 * custom element: that element is already instantiated sitewide (the cart
 * drawer section renders on every page, including /cart, via
 * layout/theme.liquid). A second instance on this page would double-fire its
 * document-level Escape-to-close and native-cart-icon "open" listeners and
 * race the real drawer. This controller uses event delegation scoped to the
 * page section's own root container instead, with distinct data-scp-* hooks
 * so its bindings can never collide with the drawer's data-sc-* elements.
 *
 * Fastrr checkout (data-sc-fastrr-checkout / data-sc-fastrr-warning) and the
 * custom-panel trigger (data-checkout-open) are deliberately left to the
 * scripts already loaded sitewide by the cart drawer section (sr-checkout.liquid
 * / sindhi-checkout.js) — both use document-level delegated listeners /
 * querySelectorAll, so this page's buttons are picked up automatically. This
 * file never touches those attributes beyond dispatching the cart:updated /
 * sindhi:cart:updated events those scripts already listen for.
 */
(() => {
  const ROUTES = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const url = (path) => `${ROUTES.replace(/\/$/, '')}${path}`;

  const fetchJSON = (path, opts = {}) =>
    fetch(url(path), {
      ...opts,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(opts.headers || {}) },
    }).then((r) => {
      if (!r.ok) throw new Error('fetch_failed');
      return r.json();
    });

  function notifyCartUpdated(cart) {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    document.dispatchEvent(new CustomEvent('sindhi:cart:updated', { detail: { cart } }));
  }

  /* CRO backlog #9 monitor (2026-07-11): a single Clarity session recording
   * showed raw JS/Liquid source ("if (typeof") rendering as visible page
   * text near the top of /cart, unreproducible on static review or a fresh
   * live fetch. Most likely trigger point is _refresh()'s DOMParser +
   * replaceWith() swap of a Section Rendering API fragment — if that
   * fragment is ever malformed (unclosed tag, stray </script>), the parser
   * can misinterpret trailing markup as plain text. This scan is passive
   * (console.error + GA4 event only, never alters the page) so it can catch
   * a recurrence without waiting for another rare session recording.
   */
  const RAW_CODE_PATTERN = /if\s*\(typeof\b|<\/script|function\s*\([^)]*\)\s*\{|=>\s*\{|document\.(querySelector|getElementById|createElement)\(/;

  function scanForRawCodeLeak(root) {
    try {
      const walker = document.createTreeWalker(root || document.body, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
          const tag = node.parentElement && node.parentElement.tagName;
          if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT' || tag === 'TEMPLATE') return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        },
      });
      let node;
      while ((node = walker.nextNode())) {
        const text = node.nodeValue;
        if (text && RAW_CODE_PATTERN.test(text)) {
          const snippet = text.trim().slice(0, 120);
          console.error('[sindhi-cart-monitor] Raw code text detected in visible DOM:', snippet);
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'raw_code_leak_detected', {
              event_category: 'cart_page_integrity',
              page_path: window.location.pathname,
              snippet,
            });
          }
          return;
        }
      }
    } catch (e) {
      /* the monitor must never itself break the page */
    }
  }

  class SindhiCartPage {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.savedKey = root.dataset.savedKey || '_saved_for_later';
      this.couponKey = root.dataset.couponKey || 'discount_code';
      this._refreshing = false;
      this._bind();
    }

    _q(sel) { return this.root.querySelector(sel); }
    _qa(sel) { return Array.from(this.root.querySelectorAll(sel)); }

    _bind() {
      // Delegated listeners on the persistent root — survive .sc-page__body /
      // .sc-page__footer being swapped out by _refresh(), so nothing needs
      // rebinding after every cart mutation.
      this.root.addEventListener('click', (e) => this._onClick(e));
      this.root.addEventListener('change', (e) => this._onChange(e));
      this.root.addEventListener('submit', (e) => this._onSubmit(e));
    }

    _onClick(e) {
      const decBtn = e.target.closest('[data-scp-qty-dec]');
      if (decBtn) {
        const wrap = decBtn.closest('[data-scp-qty]');
        const valEl = wrap && wrap.querySelector('[data-scp-qty-val]');
        const cur = parseInt((valEl && valEl.textContent) || '0', 10);
        this._changeQty(wrap.dataset.lineKey, Math.max(0, cur - 1));
        return;
      }
      const incBtn = e.target.closest('[data-scp-qty-inc]');
      if (incBtn) {
        const wrap = incBtn.closest('[data-scp-qty]');
        const valEl = wrap && wrap.querySelector('[data-scp-qty-val]');
        const cur = parseInt((valEl && valEl.textContent) || '0', 10);
        this._changeQty(wrap.dataset.lineKey, cur + 1);
        return;
      }
      const removeBtn = e.target.closest('[data-scp-remove]');
      if (removeBtn) {
        this._changeQty(removeBtn.dataset.lineKey, 0);
        return;
      }
      const restoreEl = e.target.closest('[data-scp-item-restore]');
      if (restoreEl) {
        this._toggleSaved(restoreEl.dataset.lineId, false);
        return;
      }
      const couponRemove = e.target.closest('[data-scp-coupon-remove]');
      if (couponRemove) {
        this._applyCoupon('');
        return;
      }
    }

    _onChange(e) {
      const variantSel = e.target.closest('[data-scp-variant]');
      if (variantSel) {
        this._swapVariant(variantSel.dataset.lineKey, variantSel.value);
        return;
      }
      const checkbox = e.target.closest('[data-scp-item-check]');
      if (checkbox) {
        this._toggleSaved(checkbox.dataset.lineId, !checkbox.checked);
        return;
      }
    }

    _onSubmit(e) {
      const form = e.target.closest('[data-scp-coupon-form]');
      if (!form) return;
      e.preventDefault();
      const input = form.querySelector('[data-scp-coupon-input]');
      const code = input && input.value.trim();
      if (code) this._applyCoupon(code);
    }

    async _changeQty(key, qty) {
      if (!key) return;
      await fetchJSON('/cart/change.js', { method: 'POST', body: JSON.stringify({ id: key, quantity: qty }) });
      this._refresh();
    }

    async _swapVariant(key, newVariantId) {
      const cart = await fetchJSON('/cart.js');
      const line = cart.items.find((i) => i.key === key);
      if (!line) return;
      await fetchJSON('/cart/change.js', { method: 'POST', body: JSON.stringify({ id: key, quantity: 0 }) });
      await fetchJSON('/cart/add.js', { method: 'POST', body: JSON.stringify({ items: [{ id: newVariantId, quantity: line.quantity }] }) });
      this._refresh();
    }

    async _toggleSaved(lineId, save) {
      const cart = await fetchJSON('/cart.js');
      const cur = (cart.attributes && cart.attributes[this.savedKey]) || '';
      const list = cur.split(',').filter(Boolean);
      const idx = list.indexOf(String(lineId));
      if (save && idx === -1) list.push(String(lineId));
      if (!save && idx !== -1) list.splice(idx, 1);
      await fetchJSON('/cart/update.js', { method: 'POST', body: JSON.stringify({ attributes: { [this.savedKey]: list.join(',') } }) });
      this._refresh();
    }

    async _applyCoupon(code) {
      const trimmed = (code || '').trim();
      const amountKey = `${this.couponKey}_amount`;

      if (!trimmed) {
        await fetchJSON('/cart/update.js', { method: 'POST', body: JSON.stringify({ attributes: { [this.couponKey]: '', [amountKey]: '' } }) });
        this._refresh();
        return;
      }

      /* Validate against Shopify's real discount engine, then immediately
         revert the native application. This confirms whether the code is
         genuinely active — and what it's actually worth on this cart, via
         total_discount on the same response — without changing the Shopify
         cart's own total. Fastrr still reads the coupon via the cart
         attribute below, exactly as before; only whether that attribute
         gets written now depends on real validation instead of trusting
         user input blindly. */
      let applicable = false;
      let discountAmountCents = 0;
      try {
        const result = await fetchJSON('/cart/update.js', { method: 'POST', body: JSON.stringify({ discount: trimmed }) });
        applicable = Array.isArray(result.discount_codes) &&
          result.discount_codes.some((d) => d.code === trimmed && d.applicable === true);
        if (applicable) discountAmountCents = result.total_discount || 0;
      } catch (e) {
        applicable = false;
      }
      try {
        await fetchJSON('/cart/update.js', { method: 'POST', body: JSON.stringify({ discount: '' }) });
      } catch (e) { /* best-effort revert */ }

      if (!applicable) {
        this._toast(`"${code}" isn't a valid or active code`);
        return;
      }

      await fetchJSON('/cart/update.js', {
        method: 'POST',
        body: JSON.stringify({ attributes: { [this.couponKey]: trimmed, [amountKey]: String(discountAmountCents) } }),
      });
      this._refresh();
    }

    _toast(msg) {
      const el = document.createElement('div');
      el.textContent = msg;
      el.setAttribute('role', 'status');
      el.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:#3E2723;color:#FAF6EE;padding:10px 16px;border-radius:8px;font-size:12px;z-index:10000;box-shadow:0 4px 16px rgba(0,0,0,0.2);';
      document.body.appendChild(el);
      setTimeout(() => el.remove(), 2600);
    }

    async _refresh() {
      if (this._refreshing) return;
      this._refreshing = true;
      try {
        const html = await fetch(`${window.location.pathname}?section_id=${this.sectionId}`, { headers: { Accept: 'text/html' } }).then((r) => r.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const freshBody = doc.querySelector('[data-scp-body]');
        const freshFooter = doc.querySelector('[data-scp-footer]');
        const curBody = this._q('[data-scp-body]');
        const curFooter = this._q('[data-scp-footer]');
        if (freshBody && curBody && freshFooter && curFooter) {
          curBody.replaceWith(freshBody);
          curFooter.replaceWith(freshFooter);
          scanForRawCodeLeak(this.root);
        } else {
          // Structure changed (e.g. cart just emptied, or was empty and now
          // has items) — the empty-state markup has neither node. A full
          // reload is simplest and correct here; this is a rare edge case.
          window.location.reload();
          return;
        }
        const cart = await fetchJSON('/cart.js').catch(() => null);
        if (cart) notifyCartUpdated(cart);
      } catch (e) {
        window.location.reload();
      } finally {
        this._refreshing = false;
      }
    }
  }

  function init() {
    document.querySelectorAll('[data-scp-page]').forEach((root) => {
      if (root.dataset.scpBound === '1') return;
      root.dataset.scpBound = '1';
      new SindhiCartPage(root);
      scanForRawCodeLeak(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
